import { PDF_MAX_PAGES, PDF_RENDER_MAX_SIDE } from "./constants";

export type RenderedPdfPage = {
  width: number;
  height: number;
  blob: Blob;
};

export type PdfRenderProgress = {
  phase: "loading" | "rendering" | "done";
  page: number;
  total: number;
};

let workerReady = false;

async function ensurePdfWorker(): Promise<typeof import("pdfjs-dist")> {
  const pdfjs = await import("pdfjs-dist");
  if (!workerReady) {
    pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
    workerReady = true;
  }
  return pdfjs;
}

export type OpenPdfDocument = {
  totalPages: number;
  renderPage: (page1Based: number) => Promise<RenderedPdfPage>;
  destroy: () => void;
};

/** Открыть PDF один раз — дальше renderPage по одной странице. */
export async function openPdfDocument(
  file: File,
  opts?: { maxPages?: number; maxSide?: number }
): Promise<OpenPdfDocument> {
  const maxPages = opts?.maxPages ?? PDF_MAX_PAGES;
  const maxSide = opts?.maxSide ?? PDF_RENDER_MAX_SIDE;
  const pdfjs = await ensurePdfWorker();
  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;
  const total = Math.min(doc.numPages, maxPages);
  if (doc.numPages > maxPages) {
    doc.destroy();
    throw new Error(`PDF содержит ${doc.numPages} стр. — максимум ${maxPages}`);
  }

  return {
    totalPages: total,
    async renderPage(page1Based: number) {
      const page = await doc.getPage(page1Based);
      const viewport1 = page.getViewport({ scale: 1 });
      // Всегда к maxSide (и вверх, и вниз) — иначе слайды остаются на scale=1 (~72 DPI).
      const scaled = fitToMaxSide(viewport1.width, viewport1.height, maxSide);
      const scale = scaled.w / viewport1.width;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas 2D недоступен");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      await page.render({ canvasContext: ctx, viewport, canvas }).promise;
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Не удалось создать PNG"))),
          "image/png"
        );
      });

      return { width: scaled.w, height: scaled.h, blob };
    },
    destroy() {
      doc.destroy();
    },
  };
}

/** Вписать страницу в квадрат maxSide × maxSide с сохранением пропорций. */
function fitToMaxSide(width: number, height: number, maxSide: number): { w: number; h: number } {
  const longest = Math.max(width, height);
  if (!(longest > 0) || !(maxSide > 0)) return { w: 1, h: 1 };
  const scale = maxSide / longest;
  return {
    w: Math.max(1, Math.round(width * scale)),
    h: Math.max(1, Math.round(height * scale)),
  };
}

/** Рендер страниц PDF в PNG blobs (pdf.js, dynamic import). */
export async function renderPdfPages(
  file: File,
  opts?: {
    maxPages?: number;
    maxSide?: number;
    onProgress?: (p: PdfRenderProgress) => void;
  }
): Promise<RenderedPdfPage[]> {
  const maxPages = opts?.maxPages ?? PDF_MAX_PAGES;
  const maxSide = opts?.maxSide ?? PDF_RENDER_MAX_SIDE;
  const onProgress = opts?.onProgress;

  onProgress?.({ phase: "loading", page: 0, total: 0 });
  const pdf = await openPdfDocument(file, { maxPages, maxSide });
  const total = pdf.totalPages;

  try {
    const pages: RenderedPdfPage[] = [];
    for (let i = 1; i <= total; i++) {
      onProgress?.({ phase: "rendering", page: i, total });
      pages.push(await pdf.renderPage(i));
    }
    onProgress?.({ phase: "done", page: total, total });
    return pages;
  } finally {
    pdf.destroy();
  }
}

/** Локальные blob-превью страниц PDF до загрузки ассетов на сервер. */
const previewsByObject = new Map<string, Map<number, string>>();

export function setPdfPagePreview(objectId: string, pageIndex: number, blob: Blob): string {
  let pages = previewsByObject.get(objectId);
  if (!pages) {
    pages = new Map();
    previewsByObject.set(objectId, pages);
  }
  const prev = pages.get(pageIndex);
  if (prev) URL.revokeObjectURL(prev);
  const url = URL.createObjectURL(blob);
  pages.set(pageIndex, url);
  return url;
}

export function getPdfPagePreviewUrl(objectId: string, pageIndex: number): string | null {
  return previewsByObject.get(objectId)?.get(pageIndex) ?? null;
}

export function clearAllPdfPagePreviews(objectId: string): void {
  const pages = previewsByObject.get(objectId);
  if (!pages) return;
  for (const url of pages.values()) URL.revokeObjectURL(url);
  previewsByObject.delete(objectId);
}
